import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import RecurringAdmin from "@/components/RecurringAdmin";

const DEFAULT_DISC = { landing_page: [20, 5], project_site: [30, 15], advanced: [40, 30] };
const L = ({ children }) => <div className="text-[11px] text-slate-500 mb-1">{children}</div>;

function PlanRows({ d, c, setPlan }) {
  return (
    <div className="space-y-2" data-testid="diy-plan-rows">
      {d.plans.map((p) => {
        const v = c.plans[p.key] || {};
        return (
          <div key={p.key} className="grid sm:grid-cols-[1fr_140px_140px_160px] gap-2 items-end border-t border-slate-800 pt-2">
            <div className="text-sm font-medium">{p.label}<div className="text-[11px] text-slate-500">{p.included.length} pages & features included in the base price</div></div>
            <div><L>Base price (INR)</L><Input type="number" value={v.base ?? ""} onChange={(e) => setPlan(p.key, "base", e.target.value)} data-testid={`diy-plan-base-${p.key}`} /></div>
            <div><L>Base discount %</L><Input type="number" value={v.base_discount ?? ""} onChange={(e) => setPlan(p.key, "base_discount", e.target.value)} data-testid={`diy-plan-bd-${p.key}`} /></div>
            <div><L>Page & feature discount %</L><Input type="number" value={v.item_discount ?? ""} onChange={(e) => setPlan(p.key, "item_discount", e.target.value)} data-testid={`diy-plan-id-${p.key}`} /></div>
          </div>
        );
      })}
    </div>
  );
}

function ItemPrices({ d, c, setC }) {
  return (
    <div className="max-h-96 overflow-y-auto custom-scrollbar space-y-3" data-testid="diy-item-prices">
      {d.categories.map(([cat, label]) => (
        <div key={cat}><div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">{d.items.filter((i) => i.category === cat).map((i) => (
            <div key={i.key} className="flex items-center gap-2 text-sm">
              <span className="flex-1 min-w-0 truncate">{i.label} <span className="text-[10px] font-mono text-slate-500">{i.is_page ? "PAGE" : "FEATURE"}{i.mandatory ? " · REQUIRED" : ""}</span></span>
              {!i.mandatory && <label className="flex items-center gap-1 text-[11px] text-slate-500 shrink-0"><input type="checkbox" checked={(c.quote_required || []).includes(i.key)} onChange={(e) => setC({ ...c, quote_required: e.target.checked ? [...(c.quote_required || []), i.key] : (c.quote_required || []).filter((x) => x !== i.key) })} data-testid={`diy-quote-req-${i.key}`} />Quote</label>}
              {(c.quote_required || []).includes(i.key) ? <span className="h-8 w-28 text-xs text-amber-600 flex items-center">Quote required</span> :
              <Input type="number" className="h-8 w-28" placeholder={d.plans.some((p) => p.included.includes(i.key)) ? "Auto share" : "Free"} value={c.item_prices[i.key] ?? ""} onChange={(e) => setC({ ...c, item_prices: { ...c.item_prices, [i.key]: e.target.value === "" ? null : Number(e.target.value) } })} data-testid={`diy-item-price-${i.key}`} />}
            </div>))}</div>
        </div>))}
    </div>
  );
}

export default function DiyPricingAdmin() {
  const [d, setD] = useState(null); const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/admin/diy-pricing").then((r) => {
      const cfg = r.data.config || {};
      const plans = Object.fromEntries(r.data.plans.map((p) => [p.key, { base: "", base_discount: DEFAULT_DISC[p.key][0], item_discount: DEFAULT_DISC[p.key][1], ...(cfg.plans || {})[p.key] }]));
      setD(r.data); setC({ tax_percent: 0, advance_percent: 10, recurring: [], ...cfg, plans, item_prices: cfg.item_prices || {} });
    }).catch((e) => toast.error(`DIY pricing could not load: ${errMsg(e)}`));
  }, []);
  if (!c) return null;
  const setPlan = (k, f, val) => setC({ ...c, plans: { ...c.plans, [k]: { ...c.plans[k], [f]: val } } });
  const save = async () => {
    try {
      const r = await api.put("/admin/diy-pricing", { ...c, tax_percent: Number(c.tax_percent || 0), advance_percent: Number(c.advance_percent || 0), recurring: (c.recurring || []).filter((x) => x.label).map((x) => ({ ...x, amount: Number(x.amount || 0), tax_percent: Number(x.tax_percent || 0) })) });
      setC({ ...c, ...r.data.config }); toast.success("DIY website pricing saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="lx-card space-y-4" data-testid="admin-diy-pricing">
      <div className="font-semibold">DIY AI website pricing</div>
      <p className="text-xs text-slate-500">Each plan is a starting template. Its base price covers the pages and features included in that plan; when a customer removes an included item, they are charged only for what they keep (each removed item is valued at its price below, or an equal share of the base price if left empty). Anything added beyond the plan is charged at the price you set below, minus that plan's page & feature discount. Leave a plan's base price empty to keep DIY payments off for that plan. Leave a page or feature price empty ("Auto share") to value it at an equal share of the plan base; items that no plan includes are free unless you price them.</p>
      <PlanRows d={d} c={c} setPlan={setPlan} />
      <div className="grid sm:grid-cols-2 gap-3 max-w-md">
        <div><L>Advance % before generation</L><Input type="number" value={c.advance_percent} onChange={(e) => setC({ ...c, advance_percent: e.target.value })} data-testid="diy-advance-percent" /></div>
        <div><L>Tax % (0 if not GST registered)</L><Input type="number" value={c.tax_percent} onChange={(e) => setC({ ...c, tax_percent: e.target.value })} data-testid="diy-tax-percent" /></div>
      </div>
      <div><div className="text-sm font-semibold mb-2">Price for each page and feature (INR, before the plan discount)</div><ItemPrices d={d} c={c} setC={setC} /></div>
      <RecurringAdmin c={c} setC={setC} />
      <Button onClick={save} data-testid="diy-pricing-save"><Save className="h-4 w-4 mr-1" />Save DIY pricing</Button>
    </div>
  );
}
